// analytics.js — lichtgewicht event-tracking voor configurator + landing.
//
// Public API:
//   trackEvent(type, props)  — fire-and-forget; zet event in queue
//   getSessionId()           — UUID, persist in localStorage
//   setOptOut(bool)          — disable/enable tracking volledig
//   isOptedOut()             — UI-helpers
//
// Implementatie-keuzes:
//   - Anoniem by default. Geen IP, geen vingerafdruk; alleen een
//     locale-storage UUID + soft idle-timeout van 30 min.
//   - Batched flush: max 10 events of elke 5 sec, of bij visibility-
//     change/unload (sendBeacon zodat de POST gegarandeerd wegloopt).
//   - Whitelist van event-types staat ook server-side, hier dienen
//     verkeerde namen alleen voor self-doc.
//   - Géén external dependencies — werkt in de configurator (ES-modules
//     via Vite) én op de landing-page (klassiek <script>) door een
//     dunne window-shim aan het einde te exposeeren.

const ENDPOINT = '/api/events';
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE = 10;
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 min idle = nieuwe sessie

const SESSION_KEY = 'cfg_analytics_session_v1';
const VISITOR_KEY = 'cfg_analytics_visitor_v1';
const OPTOUT_KEY  = 'cfg_analytics_optout_v1';
// Visitor-cookie/localStorage TTL: 365 dagen. Onderscheidt terugkerende
// klanten van nieuwe; wordt anoniem opgeslagen (random UUID, geen PII).
const VISITOR_TTL_MS = 365 * 24 * 60 * 60 * 1000;

// ---- session-management --------------------------------------------

function _readSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object' || !obj.id) return null;
    return obj;
  } catch { return null; }
}

function _writeSession(obj) {
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(obj)); } catch {}
}

function _newId() {
  // RFC4122-ish v4 UUID. Gebruik crypto.randomUUID waar beschikbaar.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback voor oudere browsers — niet cryptografisch sterk maar
  // genoeg voor analytics-id-uniqueness.
  const r = Math.random().toString(16).slice(2);
  return `${Date.now().toString(16)}-${r}`;
}

export function getSessionId() {
  const now = Date.now();
  let s = _readSession();
  if (!s || (now - (s.lastSeen || 0) > SESSION_TTL_MS)) {
    s = { id: _newId(), startedAt: now, lastSeen: now };
  } else {
    s.lastSeen = now;
  }
  _writeSession(s);
  return s.id;
}

/**
 * Visitor-id: persist langer dan een sessie zodat we terugkerende
 * bezoekers anoniem kunnen herkennen. Refresh de timestamp bij elke
 * lookup zodat actieve gebruikers de TTL nooit verliezen; pas na
 * 365 dagen volledige inactiviteit krijgen ze een nieuwe id.
 */
export function getVisitorId() {
  const now = Date.now();
  let v = null;
  try {
    const raw = localStorage.getItem(VISITOR_KEY);
    if (raw) v = JSON.parse(raw);
  } catch {}
  if (!v || !v.id || (now - (v.lastSeen || 0) > VISITOR_TTL_MS)) {
    v = { id: _newId(), createdAt: now, lastSeen: now };
  } else {
    v.lastSeen = now;
  }
  try { localStorage.setItem(VISITOR_KEY, JSON.stringify(v)); } catch {}
  return v.id;
}

// ---- opt-out --------------------------------------------------------

export function isOptedOut() {
  try {
    if (localStorage.getItem(OPTOUT_KEY) === '1') return true;
  } catch {}
  // Honor browser-level Do-Not-Track ook al is 'ie deprecated — vergt
  // geen extra werk voor ons en respecteert privacy-voorkeuren.
  if (typeof navigator !== 'undefined' && navigator.doNotTrack === '1') return true;
  return false;
}

export function setOptOut(value) {
  try {
    if (value) localStorage.setItem(OPTOUT_KEY, '1');
    else       localStorage.removeItem(OPTOUT_KEY);
  } catch {}
}

// ---- queue + flush --------------------------------------------------

const _queue = [];
let _flushTimer = null;
let _flushing = false;

function _scheduleFlush() {
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

/**
 * Maak een batch-payload op basis van wat in de queue staat. Gemeenschappelijke
 * velden (locale, device, page_path) worden 1× per batch meegestuurd zodat
 * we per event alleen type/props/timestamp hoeven mee te geven.
 */
function _buildPayload(rows) {
  const w = (typeof window !== 'undefined') ? window : {};
  const nav = (typeof navigator !== 'undefined') ? navigator : {};
  // Mobile detection — heuristisch maar voldoende voor segmentatie.
  let device = 'desktop';
  if (typeof w.matchMedia === 'function' && w.matchMedia('(pointer: coarse)').matches) {
    device = 'mobile';
  }
  // Locale: probeer document.documentElement.lang, anders nav.language.
  let locale = '';
  try {
    locale = (document.documentElement.lang || nav.language || '').slice(0, 8).toLowerCase();
  } catch {}
  let pagePath = '';
  try { pagePath = location.pathname.slice(0, 256); } catch {}
  return {
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    locale,
    device,
    page_path: pagePath,
    events: rows,
  };
}

/**
 * Flush de queue naar de server. Wordt automatisch geroepen door de
 * timer en bij visibility/unload. Mag manueel opgeroepen worden voor
 * tests of bij een belangrijk event waar je geen 5s wilt wachten.
 *
 * Bij unload gebruiken we navigator.sendBeacon zodat de browser
 * gegarandeerd POSTet ook al sluit het tabblad direct erna.
 */
export function flush({ useBeacon = false } = {}) {
  if (_flushing) return;
  if (_queue.length === 0) return;
  if (isOptedOut()) {
    _queue.length = 0;
    return;
  }
  const rows = _queue.splice(0, MAX_QUEUE * 5); // bonzage limiet
  const payload = _buildPayload(rows);
  const body = JSON.stringify(payload);

  if (useBeacon && typeof navigator !== 'undefined'
      && typeof navigator.sendBeacon === 'function') {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    } catch {}
    // Beacon faalde → val terug op fetch zonder keepalive (best-effort)
  }

  _flushing = true;
  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  })
    .catch(() => {
      // Re-queue alleen als de server bereikbaar leek niet — anders
      // aanvaarden we het verlies. Voor MVP: gewoon laten gaan.
    })
    .finally(() => { _flushing = false; });
}

/**
 * Track een enkel event. Props worden serialiseerbaar verwacht; we
 * zetten ze inline in de queue zonder verdere validatie (server doet
 * de whitelist).
 *
 * Voorbeelden:
 *   trackEvent('page_view');
 *   trackEvent('product_picked', { product_code: 'aom-traanplaat-2-traans' });
 *   trackEvent('hole_added',     { type: 'round', diameter: 50 });
 */
export function trackEvent(type, props) {
  if (!type) return;
  if (isOptedOut()) return;
  _queue.push({ type, props: props || null, ts: Date.now() });
  if (_queue.length >= MAX_QUEUE) flush();
  else _scheduleFlush();
}

// ---- lifecycle hooks ------------------------------------------------

if (typeof window !== 'undefined' && !window.__cowork_analytics_wired) {
  window.__cowork_analytics_wired = true;
  // Bij verbergen / unload: flush met sendBeacon zodat openstaande
  // events gegarandeerd geleverd worden.
  const onHide = () => flush({ useBeacon: true });
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') onHide();
  });
  window.addEventListener('pagehide', onHide);
  // Voor klassieke <script>-omgeving (landing page) ook een global shim
  // exposeeren zodat we daar dezelfde API kunnen gebruiken zonder ESM.
  window.coworkAnalytics = { trackEvent, flush, getSessionId, getVisitorId, setOptOut, isOptedOut };
}
